import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PasswordConfirmationErrorMatcher } from 'src/app/cadastro/confirmar-cadastro-form/password-confirmation-error-matcher.model';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { ToastService } from 'src/app/shared/toast';
import { PerfilService } from '../perfil.service';
import { CustomValidators } from './custom-validator';
import { Observable } from 'rxjs';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

type FieldsKeys = 'currentPassword' | 'newPassword' | 'confirmPassword';

@Component({
  selector: 'app-alterar-senha-perfil-page',
  templateUrl: './alterar-senha-perfil-page.component.html',
  styleUrls: ['./alterar-senha-perfil-page.component.scss']
})
export class AlterarSenhaPerfilPageComponent implements OnInit {

  form: FormGroup;
  routerState?: { isValidationAllowed: boolean };
  matcher = new PasswordConfirmationErrorMatcher();
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  private readonly HTTP_BUSSINESS_ERROR = 422;
  bussinessErrorMessage: string | null = null;
  showValidation = false;
  isMinorThanTablet: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private loader: LoaderService,
    private perfilService: PerfilService,
    private toastService: ToastService,
    private breakpointObserver: BreakpointObserver,
  ) {
  }

  ngOnInit(): void {
    this.routerState = this.router.getCurrentNavigation()?.extras?.state as { isValidationAllowed: boolean };

    this.form = this.formBuilder.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', Validators.compose([
        Validators.required,
        // 2. check whether the entered password has a number
        CustomValidators.patternValidator(/\d/, { hasNumber: true }),
        // 3. check whether the entered password has upper case letter
        CustomValidators.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
        // 4. check whether the entered password has a lower-case letter
        CustomValidators.patternValidator(/[a-z]/, { hasSmallCase: true }),
        // 5. check whether the entered password has a special character
        CustomValidators.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, { hasSpecialCharacters: true }),
        Validators.minLength(8)
      ])],
      confirmPassword: ['', [Validators.required]]
    }, { validator: this.checkPasswords });
  }

  async handleSaveNewPassword(e: Event): Promise<void> {
    e.preventDefault();
    try {
      this.loader.start();
      await this.perfilService.alterarSenha({
        senhaAtual: this.getFieldValue('currentPassword'),
        novaSenha: this.getFieldValue('newPassword'),
      });
      this.toastService.show({ title: 'Senha alterada!', text: `Senha cadastrada com sucesso.`, type: 'success' });
      this.router.navigate(['/portal/perfil']);
    } catch (responseError) {
      if (responseError && responseError.error) {
        if (responseError.status === this.HTTP_BUSSINESS_ERROR) {
          const isCurrentPasswordWrong = (responseError.error.message as string).includes('confere');
          if (isCurrentPasswordWrong) {
            this.form.get('currentPassword').setErrors({ wrongPassword: true });
          }
        } else {
          this.toastService.show({ title: 'Não foi possível alterar sua senha.', text: `Erro ao salvar as informações.`, type: 'error' });
        }
      } else {
        this.toastService.show({ title: 'Não foi possível alterar sua senha.', text: `Erro ao salvar as informações.`, type: 'error' });
      }
    } finally {
      this.loader.stop();
    }
  }

  checkPasswords(group: FormGroup): any {
    const { value: newPassword } = group.get('newPassword');
    const { value: confirmPassword } = group.get('confirmPassword');

    const isFieldValid = (confirmPassword === newPassword) || group.get('confirmPassword').hasError('required');

    return isFieldValid ? null : { passwordsDoNotMatch: true };
  }

  hasAnyError(field: FieldsKeys): boolean {
    return this.form.get(field).touched && !this.form.get(field).valid;
  }

  get passwordsNotMatch(): boolean {
    return this.form.hasError('passwordsDoNotMatch');
  }

  getFieldValue(field: FieldsKeys) {
    return this.form.get(field).value;
  }

  handleCancel() {
    this.router.navigate(['portal/perfil'], { state: { confirmDeactivate: this.form.dirty } });
  }

  getIconByErrorStatus(errorToTrack: string): { icon: string, color: string } {
    const iconsByStatus = {
      error: 'cancel',
      valid: 'check_circle',
      neutral: 'circle'
    };

    if (this.form.get('newPassword').touched && (this.form.get('newPassword').hasError('required') || this.form.get('newPassword').hasError(errorToTrack))) {
      return { icon: iconsByStatus.error, color: 'text-error' };
    }

    if (this.form.get('newPassword').dirty && !this.form.get('newPassword').hasError('required') && !this.form.get('newPassword').hasError(errorToTrack)) {
      return { icon: iconsByStatus.valid, color: 'text-valid' };
    }

    return { icon: iconsByStatus.neutral, color: 'text-neutral' };
  }

  handleGoBack() {
    this.router.navigate(['/portal/perfil/alterar/senha/confirmar-token'], { state: { confirmDeactivate: true } });
  }

}
